import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { DataGrid } from "components/DataGrid";
import { TruncateTypography } from "components/TruncateTypography";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import {
  ActivityType,
  ErrorClusterGroupsByRunIdQuery,
  ErrorClusterGroupsByRunIdQueryVariables,
  ErrorClusterRunsQuery,
  ErrorClustersByRunIdQuery,
  ErrorClustersByRunIdQueryVariables,
  ErrorsBlockingCohortQuery,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { CustomGridColDef } from "components/DataGrid";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { prettyPrintDateMed, prettyPrintDateTimeMed } from "util/dates";
import { useMutation, useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { ErrorBarChart } from "./ErrorBarChart";
import PlayArrow from "@mui/icons-material/PlayArrow";

const QUEUE_ERROR_CLUSTERING_JOB_MUTATION = gql(`
  mutation QueueErrorClusteringJob($input: QueueErrorClusteringJobInput!) {
    queueErrorClusteringJob(input: $input) {
      id
      status
    }
  }
`);

const ERROR_CLUSTER_RUNS_QUERY = gql(`
  query ErrorClusterRuns($page: Int, $pageSize: Int) {
    errorClusterRuns(page: $page, pageSize: $pageSize) {
      count
      items {
        runId
        updatedAt
        groupCount
        totalErrors
      }
    }
  }
`);

const ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY = gql(`
  query ErrorClusterGroupsByRunId($runId: String!, $search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    errorClusterGroupsByRunId(runId: $runId, search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        groupId
        errorPattern
        groupSize
        facilitiesInGroupCount
        facilityIds
      }
    }
  }
`);

const ERROR_CLUSTER_GROUPS_ALL_QUERY = gql(`
  query ErrorClusterGroupsAll($runId: String!) {
    errorClusterGroupsByRunId(runId: $runId, pageSize: 10000) {
      items {
        groupId
        errorPattern
        groupSize
        facilitiesInGroupCount
        facilityIds
      }
    }
  }
`);

const ERRORS_BLOCKING_COHORT_QUERY = gql(`
  query ErrorsBlockingCohort($runId: String!) {
    errorsBlockingCohort(runId: $runId) {
      count
      items {
        workflowId
        cohortId
        groupId
        activityType
        cohortMembers
        errorMessage
      }
    }
  }
`);

const ERROR_CLUSTERS_BY_RUN_ID_QUERY = gql(`
  query ErrorClustersByRunId($runId: String!, $search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    errorClustersByRunId(runId: $runId, search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        id
        workflowType
        runId
        groupId
        tenantId
        reportId
        facilityId
        workflowId
        activityId
        tenantName
        facilityName
        activityType
        activityDescription
        errorMessage
        reportTags
        createdAt
        createdBy
        updatedAt
        updatedBy
      }
    }
  }
`);

export type ErrorClusterGroupRow = NonNullable<
  NonNullable<
    ErrorClusterGroupsByRunIdQuery["errorClusterGroupsByRunId"]
  >["items"]
>[number];

export type ErrorClusterRow = NonNullable<
  NonNullable<ErrorClustersByRunIdQuery["errorClustersByRunId"]>["items"]
>[number];

export type ErrorBlockingCohortRow = NonNullable<
  NonNullable<ErrorsBlockingCohortQuery["errorsBlockingCohort"]>["items"]
>[number];

export function ErrorClustering() {
  const alerts = useAlerts();

  useBreadcrumb({
    label: "Error Triage",
    to: "/staff/fulfillment/error-clustering",
  });

  // Mutation for queuing error clustering job
  const [queueJob, { loading: queueJobLoading }] = useMutation(
    QUEUE_ERROR_CLUSTERING_JOB_MUTATION,
    {
      refetchQueries: [ERROR_CLUSTER_RUNS_QUERY],
      onCompleted: () => {
        alerts.success("Error clustering job queued successfully!");
      },
      onError: (error) => {
        alerts.error("Failed to queue job", error);
      },
    }
  );

  const handleQueueJob = useCallback(() => {
    queueJob({
      variables: {
        input: {},
      },
    });
  }, [queueJob]);

  // Fetch available runs
  const { data: runsData, loading: runsLoading } = useQuery(
    ERROR_CLUSTER_RUNS_QUERY,
    {
      variables: {
        page: 0,
        pageSize: 100, // Get first 100 runs for the dropdown
      },
    }
  );

  // Default to the latest run (first in the list since ordered by updatedAt DESC)
  const latestRunId = runsData?.errorClusterRuns?.items?.[0]?.runId;
  const [selectedRunId, setSelectedRunId] = useState<string | undefined>(
    latestRunId
  );

  // Update selected run when data loads
  useMemo(() => {
    if (latestRunId && !selectedRunId) {
      setSelectedRunId(latestRunId);
    }
  }, [latestRunId, selectedRunId]);

  // Fetch all grouped data for the chart
  const { data: allGroupsData, loading: allGroupsLoading } = useQuery(
    ERROR_CLUSTER_GROUPS_ALL_QUERY,
    {
      variables: {
        runId: selectedRunId ?? "",
      },
      skip: !selectedRunId,
    }
  );

  // Fetch errors blocking cohorts
  const { data: blockingCohortData, loading: blockingCohortLoading } = useQuery(
    ERRORS_BLOCKING_COHORT_QUERY,
    {
      variables: {
        runId: selectedRunId ?? "",
      },
      skip: !selectedRunId,
    }
  );

  const handleRunChange = useCallback((runId: string) => {
    setSelectedRunId(runId);
  }, []);

  const [blockingCohortSortModel, setBlockingCohortSortModel] =
    useState<GridSortModel>([{ field: "cohortMembers", sort: "desc" }]);

  const blockingCohortColumns = useMemo(
    (): CustomGridColDef<ErrorBlockingCohortRow>[] => [
      {
        field: "groupId",
        headerName: "Group ID",
        width: 120,
        sortable: true,
      },
      {
        field: "activityType",
        headerName: "Activity Type",
        width: 200,
        sortable: true,
        renderCell: ({ row }) => (
          <Typography variant="body2">
            {prettyPrintEnumValue(row.activityType)}
          </Typography>
        ),
      },
      {
        field: "cohortMembers",
        headerName: "Cohort Members",
        width: 150,
        sortable: true,
        align: "right",
        headerAlign: "right",
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight="medium">
            {row.cohortMembers}
          </Typography>
        ),
      },
      {
        field: "errorMessage",
        headerName: "Error Message",
        flex: 1,
        minWidth: 300,
        sortable: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.errorMessage}>
            <Box>
              <TruncateTypography variant="body2">
                {row.errorMessage}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 150,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Button
            component={Link}
            to={`/staff/cohorts/${row.cohortId}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            size="small"
            endIcon={<OpenInNew />}
          >
            View Cohort
          </Button>
        ),
      },
    ],
    []
  );

  const groupedColumns = useMemo(
    (): OmnisearchGridColDef<ErrorClusterGroupRow>[] => [
      {
        field: "groupId",
        headerName: "Group ID",
        width: 120,
        sortable: true,
      },
      {
        field: "errorPattern",
        headerName: "Error Pattern",
        flex: 1,
        minWidth: 200,
        sortable: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.errorPattern}>
            <Box>
              <TruncateTypography variant="body2">
                {row.errorPattern}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "groupSize",
        headerName: "Group Size",
        width: 130,
        sortable: true,
        align: "right",
        headerAlign: "right",
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight="medium">
            {row.groupSize}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 160,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          const facilitySearch = row.facilityIds
            .map((id) => `${id}`)
            .join(" | ");
          const encodedSearch = encodeURIComponent(facilitySearch);
          return (
            <Button
              component={Link}
              to={`/staff/fulfillment/facilities?omnisearch=facilityId:${encodedSearch}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="small"
              endIcon={<OpenInNew />}
            >
              View Facilities
            </Button>
          );
        },
      },
    ],
    []
  );

  const columns = useMemo(
    (): OmnisearchGridColDef<ErrorClusterRow>[] => [
      {
        field: "groupId",
        headerName: "Group ID",
        width: 100,
        sortable: true,
      },
      {
        field: "workflowType",
        headerName: "Workflow Type",
        width: 200,
        sortable: true,
        renderCell: ({ row }) => (
          <Typography variant="body2">
            {prettyPrintEnumValue(row.workflowType)}
          </Typography>
        ),
      },
      {
        field: "tenantName",
        headerName: "Tenant",
        width: 180,
        sortable: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.tenantName}>
            <Box>
              <TruncateTypography variant="body2">
                {row.tenantName}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "facilityName",
        headerName: "Facility",
        width: 180,
        sortable: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.facilityName}>
            <Box>
              <TruncateTypography variant="body2">
                {row.facilityName}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "activityType",
        headerName: "Activity Type",
        width: 200,
        sortable: true,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(ActivityType),
        renderCell: ({ row }) => (
          <Typography variant="body2">
            {prettyPrintEnumValue(row.activityType)}
          </Typography>
        ),
      },
      {
        field: "activityDescription",
        headerName: "Activity Description",
        width: 200,
        sortable: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.activityDescription ?? ""}>
            <Box>
              <TruncateTypography variant="body2">
                {row.activityDescription ?? "-"}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "errorMessage",
        headerName: "Error Message",
        flex: 1,
        minWidth: 300,
        sortable: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.errorMessage}>
            <Box>
              <TruncateTypography variant="body2">
                {row.errorMessage}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "reportTags",
        headerName: "Report Tags",
        width: 180,
        sortable: false,
        renderCell: ({ row }) => (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {row.reportTags.slice(0, 2).map((tag, idx) => (
              <Chip key={idx} label={tag} size="small" variant="outlined" />
            ))}
            {row.reportTags.length > 2 && (
              <Chip
                label={`+${row.reportTags.length - 2}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        ),
      },
    ],
    []
  );

  if (runsLoading || !selectedRunId) {
    return (
      <Stack py={3}>
        <Typography>Loading...</Typography>
      </Stack>
    );
  }

  const selectedRun = runsData?.errorClusterRuns?.items.find(
    (run) => run.runId === selectedRunId
  );

  return (
    <Stack p={2}>
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          pb: 3,
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="h5"
                component="h5"
                color="text.primary"
                pb={1}
              >
                Error Triage Dashboard
              </Typography>
              <Typography fontSize={12} component="p">
                View and analyze errors from workflow activities
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <FormControl sx={{ flex: 1, maxWidth: 600 }}>
                <InputLabel id="run-select-label">Runs</InputLabel>
                <Select
                  labelId="run-select-label"
                  value={selectedRunId}
                  label="Clustering Run"
                  onChange={(e) => handleRunChange(e.target.value)}
                >
                  {runsData?.errorClusterRuns?.items.map((run) => (
                    <MenuItem key={run.runId} value={run.runId}>
                      <Box>
                        <Typography variant="body1">
                          {prettyPrintDateTimeMed(
                            new Date(run.updatedAt).toISOString()
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {run.groupCount} groups • {run.totalErrors} errors •
                          Run ID: {run.runId.slice(0, 8)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={handleQueueJob}
                disabled={queueJobLoading}
                sx={{ mt: 1 }}
              >
                {queueJobLoading ? "Queueing..." : "Run Error Group Analysis"}
              </Button>
            </Box>

            {selectedRun && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Showing {selectedRun.groupCount} error groups with{" "}
                  {selectedRun.totalErrors} total errors
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, px: 3 }}>
            Error Distribution
          </Typography>
          <ErrorBarChart
            data={allGroupsData?.errorClusterGroupsByRunId?.items ?? []}
            loading={allGroupsLoading}
          />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, px: 3 }}>
            Errors Blocking Cohorts
          </Typography>
          <Box sx={{ px: 3 }}>
            <DataGrid<ErrorBlockingCohortRow>
              columns={blockingCohortColumns}
              rows={blockingCohortData?.errorsBlockingCohort?.items ?? []}
              loading={blockingCohortLoading}
              getRowId={(row) => `${row.workflowId}-${row.groupId}`}
              noRowsMessage="No errors blocking cohorts found for this run."
              autoHeight
              pagination
              paginationMode="client"
              sortingMode="client"
              sortModel={blockingCohortSortModel}
              onSortModelChange={setBlockingCohortSortModel}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, px: 3 }}>
            Error Groups
          </Typography>
          <OmnisearchDataGrid<
            ErrorClusterGroupsByRunIdQuery,
            ErrorClusterGroupsByRunIdQueryVariables,
            ErrorClusterGroupRow
          >
            persistenceKey="error-clustering-groups-table"
            isURLDriven={false}
            columns={groupedColumns}
            dataQuery={ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY}
            dataQueryVariables={{ runId: selectedRunId }}
            initialSortModel={[
              { field: "facilitiesInGroupCount", sort: "desc" },
            ]}
            getItems={(data) => data.errorClusterGroupsByRunId?.items ?? []}
            getCount={(data) => data.errorClusterGroupsByRunId?.count ?? 0}
            getRowId={(row) => row.groupId}
            noDataMessage="No grouped error clusters found for this run."
            excludeFilterColumns={[
              "groupId",
              "facilitiesInGroupCount",
              "actions",
            ]}
            additionalFilterColumns={[
              {
                key: "errorPattern",
                header: "Error Pattern",
                filterKeyType: "text",
              },
            ]}
            initialPageSize={25}
            rowHeight="auto"
          />
        </Box>

        <Box>
          <Typography variant="h6" sx={{ mb: 2, px: 3 }}>
            All Errors
          </Typography>
          <OmnisearchDataGrid<
            ErrorClustersByRunIdQuery,
            ErrorClustersByRunIdQueryVariables,
            ErrorClusterRow
          >
            persistenceKey="error-clustering-table"
            isURLDriven={false}
            columns={columns}
            dataQuery={ERROR_CLUSTERS_BY_RUN_ID_QUERY}
            dataQueryVariables={{ runId: selectedRunId }}
            initialSortModel={[{ field: "groupId", sort: "asc" }]}
            getItems={(data) => data.errorClustersByRunId?.items ?? []}
            getCount={(data) => data.errorClustersByRunId?.count ?? 0}
            noDataMessage="No error clusters found for this run."
            excludeFilterColumns={[
              "groupId",
              "reportTags",
              "activityDescription",
            ]}
            additionalFilterColumns={[
              {
                key: "workflowType",
                header: "Workflow Type",
                filterKeyType: "text",
              },
              {
                key: "tenantName",
                header: "Tenant",
                filterKeyType: "text",
              },
              {
                key: "facilityName",
                header: "Facility",
                filterKeyType: "text",
              },
              {
                key: "activityType",
                header: "Activity Type",
                filterKeyType: "enumAutocomplete",
              },
              {
                key: "errorMessage",
                header: "Error Message",
                filterKeyType: "text",
              },
            ]}
            initialPageSize={25}
            rowHeight="auto"
          />
        </Box>
      </Paper>
    </Stack>
  );
}
